import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextLgMd, TextStdMd, TextStdRg, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Avatar } from '@/UI/Avatar/Avatar';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { OrgPublisher } from '@/api/figmaOrgPublishing.api';

const PUBLISHING_STATUSES =
  I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.PUBLISHING_STATUSES;
const PUBLISHING_STATUS_KEYS = [
  PUBLISHING_STATUSES.PREPARING,
  PUBLISHING_STATUSES.UPLOADING,
  PUBLISHING_STATUSES.PUBLISHING,
];
const ROTATION_INTERVAL_MS = 1200;

export type TerminalState = 'publishing' | 'success' | 'failure';

interface PublishToCommunityModalTerminalProps {
  state: TerminalState;
  isPublishingAsTool?: boolean;
  thumbnailPreviewUrl: string;
  workflowName: string;
  publisherName: string;
  onCopyLink: () => void;
  onGoToCommunity: () => void;
  onGoBack: () => void;
  communityLinkReady: boolean;
  /** Set for an org publish, which has no Community link and opens the tool in Figma instead. */
  orgName?: string;
  onCopyOpenInFigmaLink?: () => void;
  onOpenInFigma?: () => void;
  openInFigmaLinkReady?: boolean;
  /** The publisher Figma credits on an org listing, from the publish response; no byline without it. */
  orgPublisher?: OrgPublisher | null;
}

const PublishToCommunityModalTerminal = ({
  state,
  isPublishingAsTool = false,
  thumbnailPreviewUrl,
  workflowName,
  publisherName,
  onCopyLink,
  onGoToCommunity,
  onGoBack,
  communityLinkReady,
  orgName,
  onCopyOpenInFigmaLink,
  onOpenInFigma,
  openInFigmaLinkReady = false,
  orgPublisher = null,
}: PublishToCommunityModalTerminalProps) => {
  const { t } = useTranslation();

  const contentGap = state === 'publishing' ? 6 : 3;
  const contentTopPad = state === 'success' ? 4 : 6;

  return (
    <FlexCol sx={{ flex: 1 }}>
      <FlexCol sx={{ flex: 1, alignItems: 'center', pt: contentTopPad, gap: contentGap, px: 3 }}>
        <Header state={state} orgName={orgName} isPublishingAsTool={isPublishingAsTool} />
        <ResultTile
          thumbnailPreviewUrl={thumbnailPreviewUrl}
          workflowName={workflowName}
          publisherName={publisherName}
          state={state}
          orgName={orgName}
          orgPublisher={orgPublisher}
        />
      </FlexCol>

      {state !== 'publishing' && (
        <FlexCenVer
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
            p: 3,
            borderTop: `1px solid ${token.border}`,
          }}
        >
          {state === 'success' && orgName ? (
            <>
              <ButtonContained
                mode="outlined"
                size="medium"
                onClick={onCopyOpenInFigmaLink}
                disabled={!openInFigmaLinkReady}
              >
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.COPY_LINK)}
              </ButtonContained>
              <ButtonContained
                mode="filled-light"
                size="medium"
                onClick={onOpenInFigma}
                disabled={!openInFigmaLinkReady}
              >
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.OPEN_IN_FIGMA)}
              </ButtonContained>
            </>
          ) : state === 'success' ? (
            <>
              <ButtonContained mode="outlined" size="medium" onClick={onCopyLink} disabled={!communityLinkReady}>
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.COPY_LINK)}
              </ButtonContained>
              <ButtonContained
                mode="filled-light"
                size="medium"
                onClick={onGoToCommunity}
                disabled={!communityLinkReady}
              >
                {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.GO_TO_COMMUNITY)}
              </ButtonContained>
            </>
          ) : (
            <ButtonContained mode="text" size="medium" onClick={onGoBack}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.FOOTER.GO_BACK)}
            </ButtonContained>
          )}
        </FlexCenVer>
      )}
    </FlexCol>
  );
};

const RotatingPublishingHeader = ({
  orgName,
  isPublishingAsTool,
}: {
  orgName?: string;
  isPublishingAsTool: boolean;
}) => {
  const { t } = useTranslation();
  const [frameIndex, setFrameIndex] = useState(0);
  const statusIndex = frameIndex % PUBLISHING_STATUS_KEYS.length;
  const statusKey = PUBLISHING_STATUS_KEYS[statusIndex];
  let statusText = t(statusKey);
  if (isPublishingAsTool && statusKey === PUBLISHING_STATUSES.PREPARING) {
    statusText = t(PUBLISHING_STATUSES.PREPARING_TOOL);
  }
  if (orgName && statusKey === PUBLISHING_STATUSES.PUBLISHING) {
    statusText = t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.PUBLISHING_STATUS, {
      orgName,
    });
  }

  useEffect(() => {
    const id = setInterval(() => {
      setFrameIndex((i) => i + 1);
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <Flex sx={{ position: 'relative', height: 24, width: '100%', justifyContent: 'center' }}>
      <AnimatePresence>
        <motion.div
          key={frameIndex}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ y: { type: 'spring', stiffness: 300, damping: 200, duration: 0.6 } }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <TextStdMd color={token.text}>{statusText}</TextStdMd>
        </motion.div>
      </AnimatePresence>
    </Flex>
  );
};

interface HeaderProps {
  state: TerminalState;
  isPublishingAsTool: boolean;
  orgName?: string;
}

const Header = ({ state, orgName, isPublishingAsTool }: HeaderProps) => {
  const { t } = useTranslation();

  if (state === 'publishing') {
    return <RotatingPublishingHeader orgName={orgName} isPublishingAsTool={isPublishingAsTool} />;
  }

  const headerKey =
    state === 'success'
      ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.SUCCESS_HEADER
      : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.FAILURE_HEADER;
  const subheaderKey =
    state === 'success'
      ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.SUCCESS_SUBHEADER
      : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.FAILURE_SUBHEADER;

  return (
    <FlexCol sx={{ gap: 1, textAlign: 'center', maxWidth: 388 }}>
      <TextLgMd color={token.text}>{t(headerKey)}</TextLgMd>
      <TextSmRg color={token.textSecondary}>
        {state === 'success' && orgName
          ? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.SUCCESS_SUBHEADER, { orgName })
          : t(subheaderKey)}
      </TextSmRg>
    </FlexCol>
  );
};

const ResultTile = ({
  thumbnailPreviewUrl,
  workflowName,
  publisherName,
  state,
  orgName,
  orgPublisher,
}: {
  thumbnailPreviewUrl: string;
  workflowName: string;
  publisherName: string;
  state: TerminalState;
  orgName?: string;
  orgPublisher: OrgPublisher | null;
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ scale: 1 }}
      animate={{ scale: state === 'success' ? 1.08 : 1 }}
      transition={{ type: 'spring', stiffness: 562, damping: 30, mass: 1, duration: 0.4 }}
    >
      <FlexCol
        sx={{
          width: 388,
          gap: 2,
          p: 2,
          background: token.bgSecondary,
          borderRadius: '12px',
          opacity: state === 'publishing' ? 0.5 : 1,
        }}
      >
        <Flex
          sx={{
            width: 356,
            height: 200,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${color.White04_T}`,
          }}
        >
          {thumbnailPreviewUrl && (
            <img
              src={thumbnailPreviewUrl}
              alt={workflowName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: state === 'failure' ? 'grayscale(1)' : undefined,
              }}
            />
          )}
        </Flex>
        <FlexCol sx={{ px: 1 }}>
          <TextStdRg color={token.text}>{workflowName}</TextStdRg>
          {orgName ? (
            // An org listing credits only the publisher Figma returned, once the publish has succeeded.
            <OrgPublisherByline publisher={state === 'success' ? orgPublisher : null} />
          ) : (
            <TextSmRg color={token.textSecondary}>
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.BY_AUTHOR, {
                name: publisherName,
              })}
            </TextSmRg>
          )}
        </FlexCol>
      </FlexCol>
    </motion.div>
  );
};

const OrgPublisherByline = ({ publisher }: { publisher: OrgPublisher | null }) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (!publisher) {
    return null;
  }

  return (
    <FlexCenVer sx={{ gap: 1 }}>
      {publisher.imgUrl && (
        <Avatar
          aria-label={publisher.name}
          src={publisher.imgUrl}
          alt={publisher.name}
          sx={{ width: 16, height: 16, fontSize: 10 }}
        >
          {isFplMigrationEnabled ? publisher.name : publisher.name.slice(0, 1).toUpperCase()}
        </Avatar>
      )}
      <TextSmRg color={token.textSecondary}>
        {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.TERMINAL.BY_AUTHOR, {
          name: publisher.name,
        })}
      </TextSmRg>
    </FlexCenVer>
  );
};

export default PublishToCommunityModalTerminal;
